// accent.ts — the Accent Lab's data + local logic. Method source: "Accent strategy — softening
// the accent before hiring season (2026-08-27)" (teacher research, Google Drive). The engine is
// SHADOWING: one consistent model voice speaks, the user repeats immediately, prosody (stress/
// rhythm) is scored FIRST and segmentals second — rhythm is the giveaway (syllable-timed L1 vs
// stress-timed English), and it is ONE habit to retrain, not forty sounds.
//
// Architecture rule from the brief: every L1 has a known top-6 checklist — this file keys drills
// by L1 so adding a language is adding a table row, never new code. Romanian is the first row.

export type AccentLang = "en" | "zh";

export interface AccentDrill {
  key: string;          // stable id, e.g. "ro-stress"
  title: string;        // "Stress-timing + schwa"
  why: string;          // one-line problem statement, shown above the drill
  sentences: string[];  // the drill bank — minimal pairs ride inside sentences so the ear hears them in flow
}

export interface L1Profile {
  l1: string;              // "Romanian"
  targetLang: AccentLang;  // what they're polishing
  drills: AccentDrill[];   // priority order — the FIRST drill is the highest-ROI one
}

// ── Romanian → English (doc §3, priority order) ───────────────────────────────
const RO_EN: L1Profile = {
  l1: "Romanian",
  targetLang: "en",
  drills: [
    {
      key: "ro-stress",
      title: "Stress-timing + schwa",
      why: "Romanian gives every syllable equal weight; English crushes everything between the beats into ə. The melody, not any sound, is the #1 giveaway.",
      sentences: [
        "Photography is a wonderful profession.",
        "The banana bread was absolutely delicious.",
        "Political economy is a fascinating subject.",
        "The photographer photographed a photogenic family.",
        "Vegetable soup is comfortable food for a miserable evening.",
        "The temperature in the laboratory was considerably lower.",
        "Her presentation on the environment was memorable.",
        "The secretary telephoned the university this afternoon.",
      ],
    },
    {
      key: "ro-th",
      title: "θ and ð — think / this",
      why: "Romanian has neither sound; they collapse into t/d or s/z. Tongue visibly between the teeth.",
      sentences: [
        "I think this theory is thoroughly worth thinking through.",
        "Three thousand feathers drifted through the thin air.",
        "Their father gathered the brothers together on Thursday.",
        "There's something soothing about the rhythm of the weather.",
        "The author thought the thirty-third theme was the strongest.",
        "Neither of them bothered with the leather tether.",
      ],
    },
    {
      key: "ro-ship-sheep",
      title: "ɪ vs iː — ship / sheep",
      why: "The two vowels collapse into one. The short one is quick and relaxed; the long one is tense and held.",
      sentences: [
        "The ship carrying sheep will leave when the crew can live with it.",
        "Please sit in this seat and fill in the form before you feel ill.",
        "He didn't win the race, but at least he was seen at the scene.",
        "This is a list of the least important bits to be cleaned.",
        "It's a pity the meal was ready before the mill was even filled.",
      ],
    },
    {
      key: "ro-r",
      title: "The soft English r",
      why: "The Romanian r taps or trills; the English one never touches — the tongue hovers.",
      sentences: [
        "The brilliant error was rarely repeated during rehearsal.",
        "Rory carried the red barrel around the crowded corridor.",
        "Her brother's terrible parrot roared through the corridor.",
        "A really rural road runs right around the reservoir.",
        "The library's rarest treasures are transferred every February.",
      ],
    },
    {
      key: "ro-final-voicing",
      title: "Final consonants stay voiced",
      why: "Dog must not become dok, bed must not become bet — hold the voicing to the very end.",
      sentences: [
        "The dog on the dock guarded the bag by the bed.",
        "I said the card was hard to grab at the edge of the ridge.",
        "His head hit the bed as the big bag slid off the bridge.",
        "Give the kid the good bread and leave the bad half in the fridge.",
        "The crowd heard a loud buzz as the judge closed the huge door.",
      ],
    },
    {
      key: "ro-ae",
      title: "æ — cat, not cut or cot",
      why: "Romanian lacks æ; it drifts to e or a. Open and forward, almost a smile.",
      sentences: [
        "The black cat sat on the flat mat in the back of the cab.",
        "That handbag matched the jacket the manager had on the rack.",
        "Half the class had a snack after the maths exam on Saturday.",
        "The happy family packed sandwiches and apples for the traffic jam.",
        "Dad's flashlight batteries lasted exactly half an hour.",
      ],
    },
  ],
};

export const L1_PROFILES: L1Profile[] = [RO_EN];

// Presenter-register free-shadowing bank (BBC/documentary cadence — longer arcs, natural
// intonation contours; for the loop once the targeted drills feel comfortable).
export const PRESENTER_SENTENCES: string[] = [
  "For thousands of years, this valley has shaped the lives of everyone who settled here.",
  "What happens next depends entirely on how quickly the temperature begins to fall.",
  "It's a remarkable story, and it begins, as so many do, with an accident.",
  "Behind these walls lies one of the most extraordinary collections in the world.",
  "The answer, when it finally came, surprised even the researchers themselves.",
  "Every morning, before the city wakes, the market is already full of life.",
  "Few places on Earth have changed as dramatically in a single generation.",
  "To understand why, we need to go back almost two hundred years.",
];

// ── Mandarin starter (beginner survival phrases; tones ARE the melody layer, so the same
//    prosody-first engine transfers directly) ─────────────────────────────────
export interface ZhPhrase {
  hanzi: string;
  pinyin: string;
  gloss: string;
}

export const ZH_SURVIVAL: ZhPhrase[] = [
  { hanzi: "你好", pinyin: "nǐ hǎo", gloss: "hello" },
  { hanzi: "谢谢", pinyin: "xiè xie", gloss: "thank you" },
  { hanzi: "不客气", pinyin: "bú kè qi", gloss: "you're welcome" },
  { hanzi: "对不起", pinyin: "duì bu qǐ", gloss: "sorry" },
  { hanzi: "没关系", pinyin: "méi guān xi", gloss: "it's okay" },
  { hanzi: "请问，洗手间在哪里？", pinyin: "qǐng wèn, xǐ shǒu jiān zài nǎ lǐ?", gloss: "excuse me, where is the bathroom?" },
  { hanzi: "多少钱？", pinyin: "duō shao qián?", gloss: "how much is it?" },
  { hanzi: "太贵了", pinyin: "tài guì le", gloss: "too expensive" },
  { hanzi: "我要这个", pinyin: "wǒ yào zhè ge", gloss: "I want this one" },
  { hanzi: "我听不懂", pinyin: "wǒ tīng bu dǒng", gloss: "I don't understand" },
  { hanzi: "请再说一遍", pinyin: "qǐng zài shuō yí biàn", gloss: "please say it again" },
  { hanzi: "我是老师", pinyin: "wǒ shì lǎo shī", gloss: "I am a teacher" },
  { hanzi: "我在学中文", pinyin: "wǒ zài xué zhōng wén", gloss: "I am learning Chinese" },
  { hanzi: "今天天气很好", pinyin: "jīn tiān tiān qì hěn hǎo", gloss: "the weather is nice today" },
  { hanzi: "我们走吧", pinyin: "wǒ men zǒu ba", gloss: "let's go" },
];

// Tone drills — the same syllable through all four tones + a neutral pair, then contrast pairs.
export const ZH_TONE_DRILLS: ZhPhrase[] = [
  { hanzi: "妈 麻 马 骂", pinyin: "mā má mǎ mà", gloss: "mother · hemp · horse · scold — the four tones" },
  { hanzi: "汤 糖 躺 烫", pinyin: "tāng táng tǎng tàng", gloss: "soup · sugar · lie down · scalding" },
  { hanzi: "买 卖", pinyin: "mǎi mài", gloss: "buy (3rd) vs sell (4th) — the classic money mistake" },
  { hanzi: "问 吻", pinyin: "wèn wěn", gloss: "ask (4th) vs kiss (3rd)" },
  { hanzi: "水饺 睡觉", pinyin: "shuǐ jiǎo · shuì jiào", gloss: "dumplings vs sleep — tones decide" },
  { hanzi: "四 十 十四 四十", pinyin: "sì shí shí sì sì shí", gloss: "four · ten · fourteen · forty" },
];

// ── The monthly benchmark passage (doc §4.5: pick ONE paragraph, never change it) ──
// Default; the user can replace it ONCE before the first recording, after which it locks.
export const DEFAULT_BENCHMARK_PASSAGE =
  "The North Wind and the Sun were disputing which was the stronger, when a traveler came along " +
  "wrapped in a warm cloak. They agreed that the one who first succeeded in making the traveler " +
  "take his cloak off should be considered stronger than the other. Then the North Wind blew as " +
  "hard as he could, but the more he blew the more closely did the traveler fold his cloak around " +
  "him; and at last the North Wind gave up the attempt. Then the Sun shined out warmly, and " +
  "immediately the traveler took off his cloak. And so the North Wind was obliged to confess that " +
  "the Sun was the stronger of the two.";
// (The North Wind and the Sun — the standard passage of phonetics research since the IPA adopted
// it; public domain, and its coverage of English sounds is exactly why it's the fixed benchmark.)

// The Mandarin fixed passage — the SAME fable (北风和太阳), which is also the standard passage of
// Chinese phonetics work; a beginner reads it slowly with the pinyin open, that's fine — the
// benchmark measures the same mouth against the same text over months, not fluency today.
export const DEFAULT_BENCHMARK_PASSAGE_ZH =
  "北风和太阳争论谁的本领大。这时来了一个穿着大衣的行人。他们约定，谁能先让行人脱下大衣，就算谁的本领大。" +
  "北风使劲地吹，可是吹得越厉害，行人把大衣裹得越紧。北风只好放弃了。接着太阳温暖地照耀着，" +
  "行人很快就把大衣脱了下来。北风只好承认，太阳比自己的本领大。";

export const benchmarkPassageFor = (lang: AccentLang): string =>
  lang === "zh" ? DEFAULT_BENCHMARK_PASSAGE_ZH : DEFAULT_BENCHMARK_PASSAGE;

// Months remaining to a goal date, floored at 0 — the "interviews Nov 2027" countdown.
export function monthsRemaining(targetISO: string): number {
  const now = new Date();
  const t = new Date(targetISO + "T00:00:00");
  return Math.max(0, (t.getFullYear() - now.getFullYear()) * 12 + (t.getMonth() - now.getMonth()));
}

// A timestamp's LOCAL calendar date — streaks live in the user's day, not UTC's (practice at 1am
// in Tallinn must credit today, not yesterday).
export const localDay = (isoTimestamp: string): string => {
  const d = new Date(isoTimestamp);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Daily streak from a set of LOCAL dates (any practice that day counts; today or yesterday keeps it alive).
export function dailyStreak(days: Set<string>): number {
  const d = new Date();
  const day = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  if (!days.has(day(d))) d.setDate(d.getDate() - 1); // today not yet practiced — count from yesterday
  let n = 0;
  while (days.has(day(d))) { n += 1; d.setDate(d.getDate() - 1); }
  return n;
}
